import React, { useState, useEffect } from 'react';
import '../CSS/Global.css';
import { useSidebar } from '../SidebarContext';
import Header from './Header';
import Sidebar from './SideBar';
import OfferingModal from './OfferingModal';
import { pdf } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import ReceiptDocument from './ReceiptDocument';
import { ArrowDown, CalendarClock, Clock4, AlertTriangle, Search, X } from "lucide-react";
import ReceiptModal from './ReceiptModal';
import { getDecryptedUserFromCookie } from './utils/getCookie';
import { decryptToken } from './utils/getToken';
import { useLocation, useNavigate } from 'react-router-dom';

const Receipt = () => {
  const [customers, setCustomers] = useState([]);
  const navigate = useNavigate()
  const today = new Date().toISOString().split("T")[0];
  const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");
const [loading, setLoading] = useState(true);


  // State Variables
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [filteredCustomers, setFilteredCustomers] = useState(customers);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [totalReceivedAmount, setTotalReceivedAmount] = useState(0);


  const [editData, setEditData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const urlad = process.env.REACT_APP_LINKADMIN
  const url = process.env.REACT_APP_LINK;
  const token = decryptToken("accessToken") || ""
  const location = useLocation()

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("openReceipt") === "true") {
      const newUrl = `${window.location.pathname}`;
      window.history.replaceState({}, "", newUrl);
      setIsModalOpen(true);
    }
  }, [location]);

  const fetchInvoices = async () => {
    try {
      const res = await fetch(`${url}/newinvoice/all/${isLoggedIn.main_id}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });
      const data = await res.json();

      if (Array.isArray(data)) {
        const sortedInvoices = data.sort((a, b) => b.order_number - a.order_number); // most recent first
        setInvoices(sortedInvoices);
      } else {
        console.warn("Unexpected response format:", data);
        setInvoices([]);
      }
    } catch (error) {
      console.error("Failed to fetch invoices:", error);
      setInvoices([]);
    }
  };
  useEffect(() => {


    fetchInvoices();
  }, []);


  // Effect to handle filtering
  useEffect(() => {
    const filtered = customers.filter((customer) =>
      Object.values(customer).some((value) =>
        String(value).toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
    setFilteredCustomers(filtered);
    setCurrentPage(1); // Reset to first page on new search
  }, [searchQuery, customers]);

  // Pagination Logic
  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
  const currentData = filteredCustomers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Handlers
  const handleSearch = (e) => setSearchQuery(e.target.value);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Handle Delete
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this receipt?")) return;

    try {
      const res = await fetch(`${url}/receipt/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });

      if (res.ok) {
        alert('Receipt deleted successfully');
        fetchReceipts(); // ✅ Refresh the list
      } else {
        alert('Failed to delete receipt');
      }
    } catch (err) {
      console.error('Error deleting receipt:', err);
      alert('An error occurred while deleting the receipt.');
    }
  };


  const fetchReceipts = async () => {
    try {
      setLoading(true);

      const user = getDecryptedUserFromCookie("gta_mainstateuser");
      const res = await fetch(`${url}/receipt/merged/${user.main_id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });

      const data = await res.json();
      setCustomers(data.reverse()); // latest first

      const total = data.reduce((acc, curr) => acc + parseFloat(curr.amount || 0), 0);
      setTotalReceivedAmount(total);

    } catch (err) {
      console.error("Error fetching receipts:", err);

    } finally {
      setLoading(false);
    }
  };
  // ✅ Call fetchReceipts on mount
  useEffect(() => {
    fetchReceipts();
  }, []);

  const getStatus = (invoice) => {
    const today = new Date().toISOString().split("T")[0];
    const dueDate = invoice.due_date;
    const pending = parseFloat(invoice.pending) || 0;

    if (pending === 0) return { label: "Paid", color: "green" };
    if (dueDate < today) return { label: "Overdue", color: "red" };
    return { label: "Pending", color: "gray" };
  };

  const dueTodayPendingTotal = invoices.reduce((acc, invoice) => {
    const dueDate = new Date(invoice.due_date);
    if (isNaN(dueDate)) return acc; // ✅ Skip if date is invalid

    const formattedDueDate = dueDate.toISOString().split("T")[0];
    return formattedDueDate === today ? acc + (parseFloat(invoice.pending) || 0) : acc;
  }, 0);

  const overduePendingTotal = invoices.reduce((acc, invoice) => {
    if (invoice.dpstatus === "Draft") return acc;
    const { label } = getStatus(invoice);
    return label === "Overdue" ? acc + (parseFloat(invoice.pending) || 0) : acc;
  }, 0);

  const toBase64 = async (url) => {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };


  const payableWithin30DaysTotal = invoices.reduce((acc, invoice) => {
    if (invoice.dpstatus === "Draft") return acc;

    const dueDate = new Date(invoice.due_date);
    if (isNaN(dueDate)) return acc; // ✅ Validate date

    const today = new Date();
    const diffInDays = (dueDate - today) / (1000 * 60 * 60 * 24);

    if (diffInDays > 0 && diffInDays <= 30) {
      const balanceDue = invoice.adjusted_pending !== undefined
        ? parseFloat(invoice.adjusted_pending)
        : parseFloat(invoice.pending) || 0;
      return acc + balanceDue;
    }

    return acc;
  }, 0);


  const downloadReceiptPDF = async (receipt) => {
    try {
      const user = JSON.parse(localStorage.getItem("2xgxgshdugyywerw12gh")) || {};
      const profileRes = await fetch(`${url}/register/single/${user.main_id}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });
      const profile = await profileRes.json();
      const profileData = profile.users
      // const settingsRes = await fetch(`${url}/setting/info?user=${isLoggedIn.mobile}`);
      // const settingsData = await settingsRes.json();

      profileData.address = profileData.office_address || "...";
      profileData.phone = profileData.mobile || "8433 803 555";
      profileData.email = profileData.email || "";
      profileData.logo = profileData.logo
      const invoice = invoices.find(inv => inv.invoice_number === receipt.invoiceNumber);


      let base64Logo = null;
      if (profileData?.profilePic) {
        const imageUrl = `${url}/logo/${profileData.profilePic}`;
        try {
          base64Logo = await toBase64(imageUrl);
        } catch (error) {
          console.warn("Logo conversion failed:", error);
          base64Logo = "https://100billiontech.com/static/media/100BILLION_TECH_name.aad194ea6504959b3f99.png"; // optional
        }
      }
      // const blob = await pdf(
      //   <InvoiceDocument invoice={invoice} profile={profileData} logoBase64={base64Logo} />
      // ).toBlob();
      // saveAs(blob, `Invoice_${invoice.invoice_number}.pdf`);

      const blob = await pdf(
        <ReceiptDocument
          receipt={receipt}
          profile={profileData}
          invoice={invoice}
          logoBase64={base64Logo}
        />
      ).toBlob();

      saveAs(blob, `Receipt_${receipt.invoiceNumber}_${receipt.receiptDate}.pdf`);
    } catch (err) {
      console.error("Error generating receipt PDF:", err);
    }
  };





  const viewReceiptPDF = async (receipt) => {
    try {
      const user = getDecryptedUserFromCookie("gta_mainstateuser");
      const profileRes = await fetch(`${url}/register/single/${user.main_id}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });
      const profile = await profileRes.json();
      const profileData = profile.users

      profileData.address = profileData.office_address || "...";
      profileData.phone = profileData.mobile || "";
      profileData.email = profileData.email || "";
      profileData.logo = profileData.logo
      profileData.orignal_logoname = profileData.orignal_logoname
      const invoice = invoices.find(inv => inv.invoice_number === receipt.invoiceNumber);



      let base64Logo = null;
      if (profileData?.profilePic) {
        const imageUrl = `${url}/logo/${profileData.profilePic}`;
        try {
          base64Logo = await toBase64(imageUrl);
        } catch (error) {
          console.warn("Logo conversion failed:", error);
          base64Logo = "https://100billiontech.com/static/media/100BILLION_TECH_name.aad194ea6504959b3f99.png"; // optional
        }
      }
      const blob = await pdf(
        <ReceiptDocument
          receipt={receipt}
          profile={profileData}
          invoice={invoice}
          logoBase64={base64Logo}
        />
      ).toBlob();
      const fileURL = URL.createObjectURL(blob);
      window.open(fileURL, "_blank");
      // saveAs(blob, `Invoice_${invoice.invoice_number}.pdf`);

      // const blob = await pdf(
      //   <ReceiptDocument
      //     receipt={receipt}
      //     profile={profileData}
      //     invoice={invoice}
      //   />
      // ).toBlob();


    } catch (err) {
      console.error("Error viewing receipt PDF:", err);
    }
  };








  return (
    <>
      <div className="oms-header">
        <h1 className="dashboard-title">Receipts</h1>
        <div
          className='text-black size-8 rounded-full flex justify-center items-center bg-gray-100 hover:bg-gray-200 cursor-pointer'
          onClick={() => setShowSearchBar(!showSearchBar)}
        >
          <Search size={18} />
        </div>
      </div>

      {showSearchBar && (
        <div className="w-full my-3 p-0">
          <div className="relative w-full">
            <input
              type="text"
              placeholder="Search Receipts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
              className="w-full border border-gray-300 rounded-full px-4 py-2 pr-10 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <button
              onClick={() => {
                setShowSearchBar(false);
                setSearchQuery('');
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              <X size={18} strokeWidth={2.5} />
            </button>
          </div>
        </div>
      )}


      <div className="w-full bg-white border border-gray-200 rounded p-3 shadow-sm">
        {/* Header */}
        <p className="text-lg font-semibold">
          Receipts Summary
        </p>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mt-3">

          {/* Total Received */}
          <div className="flex items-center gap-4 p-3 border border-gray-300 rounded-xl hover:shadow-md transition">
            <div className="flex size-10 items-center justify-center rounded-full bg-green-600 text-white">
              <ArrowDown size={20} />
            </div>

            <div>
              <p className="text-sm font-medium text-gray-700">Total Amount Received</p>
              <h3 className="text-xl font-semibold text-gray-900">
                Rs. {totalReceivedAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
              </h3>
            </div>
          </div>

          {/* Due Today */}
          <div className="p-3  border border-gray-300 rounded-xl hover:shadow-md transition flex items-center gap-4">
            <div className="flex size-10 items-center justify-center rounded-full bg-yellow-600 text-white">
              <CalendarClock size={20} />
            </div>

            <div>
              <p className="text-sm font-medium text-gray-700">Due as of Today</p>
              <h3 className="text-xl font-semibold text-gray-900">
                Rs. {Number(dueTodayPendingTotal).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
              </h3>
            </div>
          </div>

          {/* Payable within 30 days */}
          <div className="p-3 border border-gray-300 rounded-xl hover:shadow-md transition flex items-center gap-4">
            <div className="flex size-10 items-center justify-center rounded-full bg-blue-600 text-white">
              <Clock4 size={20} />
            </div>

            <div>
              <p className="text-sm font-medium text-gray-700">Payable within 30 Days</p>
              <h3 className="text-xl font-semibold text-gray-900">
                Rs. {payableWithin30DaysTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
              </h3>
            </div>
          </div>

          {/* Overdue Payment */}
          <div className="p-3 border border-gray-300 rounded-xl hover:shadow-md transition flex items-center gap-4">
            <div className="flex size-10 items-center justify-center rounded-full bg-red-600 text-white">
              <AlertTriangle size={20} />
            </div>

            <div>
              <p className="text-sm font-medium text-gray-700">Overdue Payment</p>
              <h3 className="text-xl font-semibold text-red-600">
                Rs. {overduePendingTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
              </h3>
            </div>
          </div>

        </div>
      </div>

      <div className="space-y-4 mt-5">

        {/* Loading State */}
        {loading && (
          <p className="text-center text-gray-500 py-4">Loading...</p>
        )}

        {/* No Data State */}
        {!loading && currentData.length === 0 && (
          <p className="text-center text-gray-500 py-4">No receipts created</p>
        )}

        {/* Data State */}
        {!loading && currentData.length > 0 && currentData.map((receipt, index) => (
          <div
            key={index}
            onClick={() => navigate(`/individual-receipt/${receipt.customerId}`)}
            className="
        bg-white shadow-md border border-blue-200 rounded-xl 
        p-3 mb-4 cursor-pointer transition hover:shadow-lg
        hover:border-blue-400
      "
          >
            {/* Header */}
            <div className="flex justify-between items-center mb-1 pb-1 border-b border-gray-200">
              <p className="text-sm text-gray-600">{receipt.receiptDate}</p>

              {receipt.customerName && (
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/customer-detail/${receipt.customer_id}`);
                  }}
                  className="text-sm underline text-blue-600 hover:text-blue-800 cursor-pointer"
                >
                  View Customer
                </span>
              )}
            </div>

            {/* Customer */}
            <div className="text-base font-semibold text-blue-500 flex flex-col gap-0.5 tracking-wide">
              <h2>{receipt.customerName}</h2>
            </div>

            {/* Body */}
            <div className="mt-1">
              <p className="text-gray-700 text-sm">
                <span className="font-semibold text-blue-500">Amount: </span>
                Rs. {receipt.amount || "0.00"}
              </p>
            </div>

          </div>
        ))}

      </div>


      {/* Pagination */}
      <div className="pagination">
        <button
          onClick={() => paginate(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        {Array.from({ length: totalPages }, (_, index) => (
          <button
            key={index}
            onClick={() => paginate(index + 1)}
            className={currentPage === index + 1 ? 'active' : ''}
          >
            {index + 1}
          </button>
        ))}
        <button
          onClick={() => paginate(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>

      <ReceiptModal
        isOpen={isModalOpen}
        setIsOpen={(val) => {
          setIsModalOpen(val);
          if (!val) setEditData(null);
        }}
        editData={editData}
        fetchReceipts={fetchReceipts}
        fetchInvoices={fetchInvoices}
      />

      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
        <button
          onClick={() => setIsModalOpen(true)}
          className="cssbuttons-io-button">
          <span>Create Receipt</span>
        </button>
      </div>

    </>
  );
};

export default Receipt;
